import type { ReactNode } from 'react'

/**
 * The five grounds a band can take. `plain` is the page's own white; `linen` and
 * `parchment` are two steps of the warm wash; `jewel` and `ink` are the theme's colour
 * and its dark, with the copy inverted.
 */
export type BandTone = 'plain' | 'linen' | 'parchment' | 'jewel' | 'ink'

export interface BandProps {
  children: ReactNode
  /** Defaults to `plain`. Adjacent bands should never share a tone. */
  tone?: BandTone
  /** Hairline rules top and bottom — for two bands that must keep the same tone. */
  edged?: boolean
  /** The centred fleuron above the content. One per band, at most. */
  ornament?: boolean
  /** Extra classes on the `<section>` — `pt-0` and `text-center` are the usual reasons. */
  className?: string
  /** Extra classes on the inner `.ul-container`, e.g. `wow animate__fadeInUp`. */
  containerClassName?: string
  /** For an in-page anchor. */
  id?: string
}

/**
 * A section with a ground of its own and more air around it than `.ul-section-spacing`
 * gives.
 *
 * Six sections stacked on the same white ground read as one column, so the band exists to
 * tell the eye where an idea stops — with colour, not with a heading. Tones resolve
 * through the tokens each app's theme already overrides, so a band is in the app's own
 * palette without a per-app rule anywhere.
 *
 * A component that renders its own `<section>` and `.ul-container` — `HighlightGrid`,
 * `Testimonial` — takes the tone through its `className` prop instead of being wrapped in
 * one of these; wrapping would nest two sections and pay the padding twice.
 */
export function Band({
  children,
  tone = 'plain',
  edged = false,
  ornament = false,
  className,
  containerClassName,
  id,
}: BandProps) {
  const classes = [`ul-band ul-band--${tone}`, edged ? 'ul-band--edged' : '', className ?? '']
    .filter(Boolean)
    .join(' ')

  return (
    <section className={classes} {...(id ? { id } : {})}>
      <div className={containerClassName ? `ul-container ${containerClassName}` : 'ul-container'}>
        {/* Decoration with no reading of its own. The <i> is the diamond; the hairlines
            either side of it are the pseudo-elements. */}
        {ornament && (
          <div className="ul-band-fleuron" aria-hidden="true">
            <i />
          </div>
        )}
        {children}
      </div>
    </section>
  )
}
